import Dexie from "dexie"
import { useLiveQuery } from "dexie-react-hooks"
import type { WhatsappMessage } from "@stockline/shared/types/communications/whatsapp-message"
import type { SmsMessage } from "@stockline/shared/types/communications/sms-message"
import {
  resolveAnalysisPillState,
  MESSAGE_ROW_STATUS_ANALYZING,
  type AnalysisPillState,
} from "@stockline/shared/types/communications/analysis-pill"
import { offlineDb } from "../../../lib/offline/db"
import { WHATSAPP_MESSAGE_TABLE } from "../../../lib/offline/resources/communications/whatsapp-message/db"
import { SMS_MESSAGE_TABLE } from "../../../lib/offline/resources/communications/sms-message/db"

type Channel = "whatsapp" | "sms"

// Module-level constant for referential stability — a fresh `new Map()` on
// every render would defeat downstream memoisation.
const EMPTY_MAP: ReadonlyMap<string, AnalysisPillState | null> = new Map()

async function lastInboundStatus(
  channel: Channel,
  chatId: string
): Promise<number | null> {
  // Reverse compound-index scan to the newest inbound, non-deleted row.
  // NOTE: rows whose timestamp IS NULL are absent from the compound index
  // (optimistic-write window before the timestamp backfill); the dot
  // self-heals on the next sync.
  if (channel === "whatsapp") {
    const row = await offlineDb
      .table<WhatsappMessage>(WHATSAPP_MESSAGE_TABLE)
      .where("[chatUid+timestamp]")
      .between([chatId, Dexie.minKey], [chatId, Dexie.maxKey])
      .reverse()
      .filter((m) => !m.isFromMe && !m.deletedAt)
      .first()
    return row ? row.status : null
  }
  const row = await offlineDb
    .table<SmsMessage>(SMS_MESSAGE_TABLE)
    .where("[chatId+timestamp]")
    .between([chatId, Dexie.minKey], [chatId, Dexie.maxKey])
    .reverse()
    .filter((m) => m.direction === "inbound" && !m.deletedAt)
    .first()
  return row ? row.status : null
}

/**
 * Chat-list analysis-dot rollup (spec §7, STK-357): per chat the dot is the
 * pill state of its LAST INBOUND message, overridden to "analyzing" if ANY
 * message in the chat is currently ANALYZING(1). Pure over the Dexie message
 * rows — no execution plumbing. Exported separately from the hook so it can
 * be unit-tested without React.
 */
export async function computeAnalysisDots(
  channel: Channel,
  chatIds: readonly string[]
): Promise<Map<string, AnalysisPillState | null>> {
  const result = new Map<string, AnalysisPillState | null>()
  if (chatIds.length === 0) return result
  const wanted = new Set(chatIds)

  // 1. One indexed read: which of the wanted chats has ANY analyzing row.
  //    Only INBOUND, non-deleted rows count — analysis is inbound-only, so an
  //    outbound row stuck at ANALYZING (historical data / a future bug) must
  //    not paint the whole chat "analyzing". Mirrors `lastInboundStatus`.
  const analyzing = new Set<string>()
  if (channel === "whatsapp") {
    const rows = await offlineDb
      .table<WhatsappMessage>(WHATSAPP_MESSAGE_TABLE)
      .where("status")
      .equals(MESSAGE_ROW_STATUS_ANALYZING)
      .toArray()
    for (const r of rows) {
      if (r.isFromMe || r.deletedAt) continue
      if (r.chatUid && wanted.has(r.chatUid)) analyzing.add(r.chatUid)
    }
  } else {
    const rows = await offlineDb
      .table<SmsMessage>(SMS_MESSAGE_TABLE)
      .where("status")
      .equals(MESSAGE_ROW_STATUS_ANALYZING)
      .toArray()
    for (const r of rows) {
      if (r.direction !== "inbound" || r.deletedAt) continue
      if (r.chatId && wanted.has(r.chatId)) analyzing.add(r.chatId)
    }
  }

  // 2. Per chat: last-inbound pill, with the analyzing override.
  await Promise.all(
    chatIds.map(async (chatId) => {
      const rowStatus = await lastInboundStatus(channel, chatId)
      const base = resolveAnalysisPillState({ rowStatus, isInbound: true })
      result.set(chatId, analyzing.has(chatId) ? "analyzing" : base)
    })
  )
  return result
}

/**
 * Reactive chat-list dot map. One shared liveQuery keyed on the visible chat
 * ids (dynamic per mount — no stable shared cache key, so `useSharedLiveQuery`
 * is intentionally not used).
 */
export function useAnalysisDotByChat(
  channel: Channel,
  chatIds: readonly string[]
): ReadonlyMap<string, AnalysisPillState | null> {
  const depKey = chatIds.join("|")
  return (
    useLiveQuery(
      () => computeAnalysisDots(channel, chatIds),
      [channel, depKey]
    ) ?? EMPTY_MAP
  )
}
